import React, { useState, useEffect } from 'react';
import { Officer } from '../data/mockData';
import { colors } from '../theme';
import api from '../services/api';

const Officers: React.FC = () => {
  const [allOfficers, setAllOfficers] = useState<Officer[]>([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.users.getAll()
      .then(res => {
        const data = res.data || res;
        if (Array.isArray(data)) setAllOfficers(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ['all', ...Array.from(new Set(allOfficers.map(o => o.category)))];

  const filtered = allOfficers.filter(o => {
    if (categoryFilter !== 'all' && o.category !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return o.name.toLowerCase().includes(q) || o.cfmsId.toLowerCase().includes(q) || o.designation.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>Officers</h2>
            <p>{allOfficers.length} registered distinguished officers</p>
          </div>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <span>🔍</span>
            <input placeholder="Search by name, CFMS ID..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        <div className="card">
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #E8ECF2' }}>
            <div className="filter-bar">
              {categories.map(c => (
                <button key={c} className={`filter-chip${categoryFilter === c ? ' active' : ''}`}
                  onClick={() => setCategoryFilter(c)}>
                  {c === 'all' ? 'All Categories' : c.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="card-body">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Officer</th>
                    <th>CFMS ID</th>
                    <th>Category</th>
                    <th>Department</th>
                    <th>Family</th>
                    <th>Active LOCs</th>
                    <th>Status</th>
                    <th>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(o => (
                    <tr key={o.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: `linear-gradient(135deg, ${colors.gold}, ${colors.goldLight})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 700, color: colors.navy900, flexShrink: 0
                          }}>
                            {o.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{o.title} {o.name}</div>
                            <div style={{ fontSize: 12, color: colors.textMuted }}>{o.designation}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}>{o.cfmsId}</td>
                      <td>
                        <span style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                          background: colors.goldSoft, color: colors.goldDark
                        }}>{o.category.replace('_', ' ')}</span>
                      </td>
                      <td style={{ fontSize: 12, maxWidth: 200 }}>{o.department}</td>
                      <td style={{ textAlign: 'center' }}>{o.familyCount}</td>
                      <td style={{ textAlign: 'center' }}>
                        {o.activeLOCs > 0 ? (
                          <span style={{ fontWeight: 700, color: colors.info }}>{o.activeLOCs}</span>
                        ) : (
                          <span style={{ color: colors.textMuted }}>0</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${o.status === 'active' ? 'verified' : 'draft'}`}>
                          <span className="badge-dot" />
                          {o.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: colors.textMuted }}>
                        {new Date(o.lastLogin).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Officers;
